import React from "react";
import I18n from 'i18n.js.erb'
import {
  CURRENCY_EURO,
  isPresent,
  parseAmount,
  hasValidVoucher,
  insuranceServiceEnabled,
  homePickUpServiceEnabled,
  returnShipmentServiceEnabled
} from "../../utils/utils";
import PDFGenerator from "./PDFGenerator";
import IconTooltip from "./IconTooltip";


// Il messaggio sulla spedizione gratuita cambia in base ai servizi offerti dalla casa di grading
const freeShippingTooltipKey = (submissionList) => {
  const homePickUp = isPresent(submissionList.grader_company?.home_pick_up_method)
  const returnDelivery = isPresent(submissionList.grader_company?.return_delivery_method)
  if (homePickUp && returnDelivery) return 'mainTitle_2'
  return homePickUp ? 'mainTitle_3' : 'mainTitle_4'
}


// Blocco degli importi di un ordine: lo stesso markup serve sia per gli importi dell'utente
// sia, sugli ordini di un aggregatore, per quelli che l'aggregatore applica al suo cliente finale.
// Cambia solo il prefisso dei campi da leggere ('customer_' per gli importi del cliente finale)
const SubmissionListAmounts = ({
  submissionList,
  sectionId,
  sectionTitle,
  prefix,
  pdfText,
  pdfPricingFor,
  setShowErrorModal
}) => (
  <div className="col-12 slx-section" id={sectionId}>
    <h2 className="slx-section-title">
      {sectionTitle}
    </h2>

    <div className="slx-amounts">
      <div className="slx-amounts__grid">
        <div className="slx-amounts__col">
          {/* Importo relativo ai servizi di grading degli articoli */}
          <div className="slx-amounts__cell">
            <p className="slx-label">
              {I18n.t('js.editSubmissionListPage.webComponent.draft.payment.gradingServices').toUpperCase()}
            </p>
            <p className="slx-value--title">
              {parseAmount(submissionList[`${prefix}grading_service_amount_vat_excluded`], 2, true)}{CURRENCY_EURO}
            </p>
          </div>

          {/* Eventuale sconto applicato dall'utente sull'importo relativo ai servizi di grading */}
          <div className="slx-amounts__cell">
            <p className="slx-label">
              {I18n.t('js.editSubmissionListPage.webComponent.draft.payment.gradingServicesDiscount').toUpperCase()}
            </p>
            <p className="slx-value--title">
              {
                hasValidVoucher(submissionList) ?
                  '-' + (
                    submissionList.voucher.amount > 0 ?
                      parseAmount(submissionList.voucher.amount, 2) + CURRENCY_EURO
                      :
                      (submissionList.voucher.percentage || 0) + '%'
                  )
                  :
                  '--'
              }
            </p>
          </div>

          {/* Importo relativo alle varie spedizioni (ritiro a domicilio della sub e/o consegna di ritorno) */}
          <div className="slx-amounts__cell">
            <p className="slx-label slx-amounts__label-with-icon">
              {
                submissionList.free_shipping ?
                  I18n.t('js.editSubmissionListPage.webComponent.draft.freeShipping.label').toUpperCase()
                  :
                  I18n.t('js.editSubmissionListPage.webComponent.draft.payment.shipping').toUpperCase()
              }
              {
                submissionList.free_shipping &&
                <IconTooltip
                  tooltipContent={I18n.t(`js.editSubmissionListPage.webComponent.draft.freeShipping.${freeShippingTooltipKey(submissionList)}`)}
                  customComponentToRender={<img src="/images/icons/free_shipping_info.svg" alt="" />}
                  customComponentToRenderCssClass="slx-amounts__label-icon"
                />
              }
            </p>
            <p className="slx-value--title">
              {
                submissionList.free_shipping ?
                  '\u00a0'
                  :
                  (homePickUpServiceEnabled(submissionList) || returnShipmentServiceEnabled(submissionList)) ?
                    parseAmount(submissionList[`${prefix}global_shipping_cost_vat_excluded`], 2, true) + CURRENCY_EURO
                    :
                    '--'
              }
            </p>
          </div>
        </div>

        <div className="slx-amounts__col">
          {/* Importo relativo al servizio di assicurazione del pacco durante le varie spedizioni */}
          <div className="slx-amounts__cell">
            <p className="slx-label">
              {I18n.t('js.editSubmissionListPage.webComponent.draft.payment.shippingInsurance').toUpperCase()}
            </p>
            <p className="slx-value--title">
              {
                insuranceServiceEnabled(submissionList) ?
                  parseAmount(submissionList[`${prefix}insurance_cost_vat_excluded`], 2, true) + CURRENCY_EURO
                  :
                  '--'
              }
            </p>
          </div>

          {/* Sub totale iva esclusa */}
          <div className="slx-amounts__cell">
            <p className="slx-label">
              {I18n.t('js.editSubmissionListPage.webComponent.draft.payment.subtotalVatExcluded').toUpperCase()}
            </p>
            <p className="slx-value--title">
              {parseAmount(submissionList[`${prefix}all_services_amount_vat_excluded`], 2, true)}{CURRENCY_EURO}
            </p>
          </div>

          {/* Sub totale iva inclusa */}
          <div className="slx-amounts__cell">
            <p className="slx-label">
              {I18n.t('js.editSubmissionListPage.webComponent.draft.payment.subtotalVatIncluded').toUpperCase()}
            </p>
            <p className="slx-value--title">
              {parseAmount(submissionList[`${prefix}all_services_amount`], 2, true)}{CURRENCY_EURO}
            </p>
          </div>
        </div>

        <div className="slx-amounts__col">
          {/* Aliquota IVA */}
          <div className="slx-amounts__cell">
            <p className="slx-label">
              {I18n.t('js.editSubmissionListPage.webComponent.draft.vatRate').toUpperCase()}
            </p>
            <p className="slx-value--title">
              {submissionList[`${prefix}vat_rate`]}%
            </p>
          </div>

          {/* Importo IVA */}
          <div className="slx-amounts__cell">
            <p className="slx-label">
              {I18n.t('js.editSubmissionListPage.webComponent.draft.vatAmount').toUpperCase()}
            </p>
            <p className="slx-value--title">
              {parseAmount(submissionList[`${prefix}vat_amount`], 2, true)}{CURRENCY_EURO}
            </p>
          </div>

          {/* Eventuale credito usato dall'utente per pagare la submission list */}
          <div className="slx-amounts__cell">
            <p className="slx-label">
              {I18n.t('js.editSubmissionListPage.webComponent.draft.payment.piggyBankUsed').toUpperCase()}
            </p>
            <p className="slx-value--title">
              -{parseAmount(submissionList.user_credit, 2, true)}{CURRENCY_EURO}
            </p>
          </div>
        </div>
      </div>

      {/* TOTALE FINALE: link al PDF a sinistra, totale allineato a destra */}
      <div className="slx-amounts__total">
        <PDFGenerator
          type={'button'}
          generatorText={pdfText.toUpperCase()}
          cssClassList={"slx-pill slx-pill--ghost border-none"}
          pdfUrl={`/submission_lists/${submissionList.id}.pdf${pdfPricingFor ? `?pricing_for=${pdfPricingFor}` : ''}`}
          setShowErrorModal={setShowErrorModal}
          customImageIcon={window.location.origin + `/images/icons/blue/download-pdf.svg`}
          spinnerCssClassList={'maxH-17px max-w-17px'}
          downloadMsgCssClassList={'text-uppercase'}
        />

        <div className="slx-amounts__total-value">
          <p className="slx-label slx-label--medium">
            {I18n.t('js.editSubmissionListPage.webComponent.draft.payment.amountVatIncluded').toUpperCase()}
          </p>
          <p className="slx-value--title">
            {parseAmount(submissionList[`${prefix}amount`], 2, true)}{CURRENCY_EURO}
          </p>
        </div>
      </div>
    </div>
  </div>
);

export default SubmissionListAmounts;
